//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { createContext, useContext, ReactNode } from 'react';

interface SolutionsApp {
  appId?: string;
}

interface ServiceProvider {
  name: string;
}

interface EditConfig {
  manage_users_teamspace: {
    show: boolean;
  };
  manage_users_loop: {
    show: boolean;
  };
}

interface Services {
  provider: ServiceProvider;
  license: {
    url: string;
  };
  loop?: {
    url: string;
    show?: boolean;
  };
  tenant: {
    url: string;
    show?: boolean;
  };
  analyticsai?: {
    url: string;
    socketUrl: string;
    show?: boolean;
  };
  dashboards: {
    url: string;
    show?: boolean;
  };
  disable: {
    show: true;
  };
  edit?: EditConfig;
  delete_user?: {
    show: boolean;
  };
  search?: {
    show: boolean;
  };
  stopPrompt?: {
    show: boolean;
  };
  darkMode?: {
    show: boolean;
  };
  diagnosticArchives?: {
    show: boolean;
  };
  azureEmbeddingDeployment?: {
    show: boolean;
  };
  ollamaProvider?: {
    show: boolean;
  };
  voiceInput?: {
    show: boolean;
  };
  platformVersionNumber?: {
    version: string;
  };
  platformBuildNumber?: {
    version: string;
  };
}

interface OIDCConfig {
  issuer: string;
  authorization: string;
  token: string;
  userinfo: string;
  introspection: string;
  jwks: string;
  logout: string;
  clientId: string;
  redirect_uri: string;
  scopes: string;
  mintingInteractiveClientId?: string;
  mintingRedirectUri?: string;
}

interface PollingConfig {
  interval: number;
  maxRetries: number;
  INACTIVITY_TIMEOUT: number;
  TOKEN_TIME_REMAINING_THRESHOLD: number;
  TOKEN_EXPIRY_CHECK_INTERVAL: number;
  SESSION_CHECK_INTERVAL: number;
}

interface Config {
  baseName: string;
  redirectUrl?: string;
  solutions?: boolean | SolutionsApp;
  services?: Services;
  oidc: OIDCConfig;
  polling?: PollingConfig;
}

const ConfigContext = createContext<Config>({
  baseName: '/',
  redirectUrl: '/',
  solutions: false,
  services: {
    provider: {
      name: '',
    },
    license: {
      url: 'http://localhost:8086/rest',
    },
    loop: {
      url: '',
      show: true,
    },
    tenant: {
      url: '',
      show: true,
    },
    analyticsai: {
      url: '',
      socketUrl: '',
      show: true,
    },
    dashboards: {
      url: '',
      show: false,
    },
    disable: {
      show: true,
    },
    edit: {
      manage_users_teamspace: {
        show: true,
      },
      manage_users_loop: {
        show: true,
      },
    },
    delete_user: {
      show: true,
    },
    search: {
      show: true,
    },
    stopPrompt: {
      show: false,
    },
    darkMode: {
      show: false,
    },
    diagnosticArchives: {
      show: false,
    },
    ollamaProvider: {
      show: false,
    },
    voiceInput: {
      show: true,
    },
  },
  oidc: {
    issuer: '',
    authorization: '',
    token: '',
    userinfo: '',
    introspection: '',
    jwks: '',
    logout: '',
    clientId: '',
    redirect_uri: '',
    scopes: '',
    mintingInteractiveClientId: '',
    mintingRedirectUri: '',
  },
  polling: {
    interval: 0,
    maxRetries: 0,
    INACTIVITY_TIMEOUT: 0,
    TOKEN_TIME_REMAINING_THRESHOLD: 0,
    TOKEN_EXPIRY_CHECK_INTERVAL: 0,
    SESSION_CHECK_INTERVAL: 0,
  },
});

export const ConfigProvider: React.FC<{
  children: ReactNode;
  config: Config;
}> = ({ children, config }) => {
  return (
    <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>
  );
};

export const useConfig = () => useContext(ConfigContext);
